import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useLazyGetUserByIdQuery } from '../../../api/users'
import { extractApiErrorMessage } from '../../../utils/api'
import {
  formatRestrictionUserLabel,
  formatRestrictionUserSlug,
  mapCandidateToSelectedUser,
  mapResolvedUserToSelected,
  parseUserSlugInput,
  type RestrictionUserCandidate,
  type SelectedRestrictionUser,
} from './restrictionUserUtils'

const fieldSx = {
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    bgcolor: 'background.paper',
    px: 0.5,
    borderRadius: 0.5,
  },
  '& .MuiOutlinedInput-root': { minHeight: 40 },
} as const

export type RestrictionUserPickerCopy = {
  emptyHint: string
  withSuggestionsHint: string
  suggestionsTitle: string
  suggestionsSubtitle: string
}

type Props = {
  selectedUser: SelectedRestrictionUser | null
  onSelectedUserChange: (user: SelectedRestrictionUser | null) => void
  candidates: RestrictionUserCandidate[]
  isFetching: boolean
  copy: RestrictionUserPickerCopy
  submitError?: string | null
}

export function RestrictionUserPicker({
  selectedUser,
  onSelectedUserChange,
  candidates,
  isFetching,
  copy,
  submitError = null,
}: Props) {
  const [fetchUserBySlug, fetchUserState] = useLazyGetUserByIdQuery()
  const [slugQuery, setSlugQuery] = useState('')
  const [slugError, setSlugError] = useState<string | null>(null)

  const hasSuggestions = candidates.length > 0

  const handleClearSelection = () => {
    onSelectedUserChange(null)
    setSlugQuery('')
    setSlugError(null)
  }

  const handleResolveSlug = async () => {
    const slug = parseUserSlugInput(slugQuery)
    if (!slug) {
      setSlugError('Укажите имя профиля (@...) или ссылку на профиль')
      return
    }

    setSlugError(null)
    try {
      const user = await fetchUserBySlug(slug).unwrap()
      const mapped = mapResolvedUserToSelected(user)
      if (!mapped) {
        onSelectedUserChange(null)
        setSlugError('Пользователь не найден')
        return
      }
      onSelectedUserChange(mapped)
      setSlugQuery('')
    } catch (error) {
      onSelectedUserChange(null)
      setSlugError(extractApiErrorMessage(error) ?? 'Пользователь не найден')
    }
  }

  const handleSelectCandidate = (candidate: RestrictionUserCandidate) => {
    if (!candidate.restrictable) return
    const mapped = mapCandidateToSelectedUser(candidate)
    if (!mapped) return
    onSelectedUserChange(mapped)
    setSlugQuery('')
    setSlugError(null)
  }

  if (selectedUser) {
    return (
      <Stack gap={1}>
        <Typography variant="black-14-medium">Пользователь</Typography>
        <Stack
          direction="row"
          gap={1.5}
          alignItems="center"
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}
        >
          <Avatar src={selectedUser.avatar ?? undefined} sx={{ width: 36, height: 36 }} />
          <Stack minWidth={0} flex={1}>
            <Typography variant="black-14-normal">{formatRestrictionUserLabel(selectedUser)}</Typography>
            {formatRestrictionUserSlug(selectedUser) ? (
              <Typography variant="black-12-light" color="text.secondary">
                {formatRestrictionUserSlug(selectedUser)}
              </Typography>
            ) : null}
          </Stack>
          <Button variant="text" size="small" onClick={handleClearSelection} sx={{ flexShrink: 0 }}>
            Сменить
          </Button>
        </Stack>
        {submitError ? (
          <Typography variant="black-12-light" color="error">
            {submitError}
          </Typography>
        ) : null}
      </Stack>
    )
  }

  return (
    <Stack gap={1.5}>
      <Stack gap={0.5}>
        <Typography variant="black-14-medium">Пользователь</Typography>
        <Typography variant="black-12-light" color="text.secondary">
          {hasSuggestions ? copy.withSuggestionsHint : copy.emptyHint}
        </Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems="stretch">
        <TextField
          placeholder="Например: @ivan-ivanov"
          value={slugQuery}
          onChange={(e) => {
            setSlugQuery(e.target.value)
            if (slugError) setSlugError(null)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void handleResolveSlug()
            }
          }}
          size="small"
          fullWidth
          sx={fieldSx}
          error={Boolean(slugError)}
          inputProps={{
            'aria-label': 'Имя профиля или ссылка на профиль пользователя',
          }}
        />
        <Button
          variant="follow"
          onClick={() => void handleResolveSlug()}
          disabled={fetchUserState.isFetching || slugQuery.trim() === ''}
          sx={{
            flexShrink: 0,
            minHeight: 40,
            px: 2.5,
            alignSelf: { xs: 'stretch', sm: 'auto' },
          }}
        >
          {fetchUserState.isFetching ? 'Ищем…' : 'Найти'}
        </Button>
      </Stack>

      {slugError ? (
        <Typography variant="black-12-light" color="error">
          {slugError}
        </Typography>
      ) : null}

      {submitError ? (
        <Typography variant="black-12-light" color="error">
          {submitError}
        </Typography>
      ) : null}

      {isFetching ? (
        <Stack direction="row" gap={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="black-12-light" color="text.secondary">
            Загружаем подсказки…
          </Typography>
        </Stack>
      ) : null}

      {!isFetching && hasSuggestions ? (
        <Stack gap={0.75}>
          <Typography variant="black-14-medium">{copy.suggestionsTitle}</Typography>
          <Typography variant="black-12-light" color="text.secondary">
            {copy.suggestionsSubtitle}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
            {candidates.map((candidate) => {
              const label = formatRestrictionUserLabel(candidate)
              const chip = (
                <Chip
                  key={candidate.id}
                  label={label}
                  size="small"
                  clickable={candidate.restrictable}
                  disabled={!candidate.restrictable}
                  onClick={() => handleSelectCandidate(candidate)}
                  sx={{ maxWidth: '100%' }}
                />
              )

              if (!candidate.restrictable && candidate.restrictable_reason) {
                return (
                  <Tooltip key={candidate.id} title={candidate.restrictable_reason}>
                    <span>{chip}</span>
                  </Tooltip>
                )
              }

              return chip
            })}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  )
}
